import { createSlice, createEntityAdapter, createSelector } from '@reduxjs/toolkit';

// Entity adapter for normalized state — O(1) lookups by alert_id
const alertsAdapter = createEntityAdapter({
  selectId: (alert) => alert.alert_id,
  sortComparer: (a, b) => new Date(a.timestamp) - new Date(b.timestamp),
});

const initialCounts = {
  total: 0,
  suppressed: 0,
  regular: 0,
  byPriority: {
    Critical: 0,
    Normal: 0,
    Low: 0,
  },
  byStatus: {
    new: 0,
    investigating: 0,
    resolved: 0,
  },
};

const initialState = alertsAdapter.getInitialState({
  alertCounts: { ...initialCounts },
  suppressionProfiles: [],
  activeProfile: null,
  viewSettings: {
    suppressionFilter: 'all', // 'all', 'normal', 'suppressed'
    viewMode: 'realtime', // 'realtime', 'mixed', 'lanes', 'dedicated'
  },
  loading: false,
  error: null,
  lastUpdated: null,
  connectionStatus: 'disconnected', // 'connecting', 'connected', 'disconnected', 'error'
  pendingUpdates: [],
});

// Full recalculation — only used for bulk loads (setAlerts) and manual refresh
const calculateAlertCounts = (alerts) => {
  const counts = {
    total: alerts.length,
    suppressed: 0,
    regular: 0,
    byPriority: {
      Critical: 0,
      Normal: 0,
      Low: 0,
    },
    byStatus: {
      new: 0,
      investigating: 0,
      resolved: 0,
    },
  };

  alerts.forEach((alert) => {
    if (alert.is_suppressed) {
      counts.suppressed++;
    } else if (alert.status !== 'resolved') {
      counts.regular++;
    }

    if (alert.priority && counts.byPriority.hasOwnProperty(alert.priority)) {
      counts.byPriority[alert.priority]++;
    }

    if (alert.status && counts.byStatus.hasOwnProperty(alert.status)) {
      counts.byStatus[alert.status]++;
    }
  });

  return counts;
};

// Incremental counter helpers — O(1) per alert change
const incrementCounters = (counts, alert) => {
  counts.total++;
  if (alert.is_suppressed) {
    counts.suppressed++;
  } else if (alert.status !== 'resolved') {
    counts.regular++;
  }
  if (alert.priority && counts.byPriority.hasOwnProperty(alert.priority)) {
    counts.byPriority[alert.priority]++;
  }
  if (alert.status && counts.byStatus.hasOwnProperty(alert.status)) {
    counts.byStatus[alert.status]++;
  }
};

const decrementCounters = (counts, alert) => {
  counts.total = Math.max(0, counts.total - 1);
  if (alert.is_suppressed) {
    counts.suppressed = Math.max(0, counts.suppressed - 1);
  } else if (alert.status !== 'resolved') {
    counts.regular = Math.max(0, counts.regular - 1);
  }
  if (alert.priority && counts.byPriority.hasOwnProperty(alert.priority)) {
    counts.byPriority[alert.priority] = Math.max(0, counts.byPriority[alert.priority] - 1);
  }
  if (alert.status && counts.byStatus.hasOwnProperty(alert.status)) {
    counts.byStatus[alert.status] = Math.max(0, counts.byStatus[alert.status] - 1);
  }
};

// Merge suppression fields, preferring new values but falling back to existing
const mergeSuppression = (existing, incoming) => ({
  ...existing,
  ...incoming,
  is_suppressed: incoming.is_suppressed ?? existing.is_suppressed,
  suppression_rule_name: incoming.suppression_rule_name ?? existing.suppression_rule_name,
  suppression_count: incoming.suppression_count ?? existing.suppression_count,
  suppression_metadata: incoming.suppression_metadata ?? existing.suppression_metadata,
  suppressed_by_profile: incoming.suppressed_by_profile ?? existing.suppressed_by_profile,
  suppressed_at: incoming.suppressed_at ?? existing.suppressed_at,
});

const alertSlice = createSlice({
  name: 'alerts',
  initialState,
  reducers: {
    // Bulk load — full recalculation (used by REST API initial fetch)
    setAlerts: (state, action) => {
      const raw = Array.isArray(action.payload)
        ? action.payload
        : action.payload?.results || action.payload?.alerts || [];
      // Filter out alerts with missing alert_id to prevent entity adapter corruption
      const alerts = raw.filter((a) => a.alert_id != null);
      alertsAdapter.setAll(state, alerts);
      state.alertCounts = calculateAlertCounts(alerts);
      state.lastUpdated = new Date().toISOString();
      state.loading = false;
      state.error = null;
    },

    // Merge alerts into existing state (used by WebSocket initial_data)
    // Uses upsertMany instead of setAll to avoid racing REST API
    mergeAlerts: (state, action) => {
      const raw = Array.isArray(action.payload)
        ? action.payload
        : action.payload?.alerts || [];
      const alerts = raw.filter((a) => a.alert_id != null);
      if (alerts.length > 0) {
        alertsAdapter.upsertMany(state, alerts);
        const allAlerts = alertsAdapter.getSelectors().selectAll(state);
        state.alertCounts = calculateAlertCounts(allAlerts);
        state.lastUpdated = new Date().toISOString();
      }
    },

    // Single alert add — O(1) counter update
    addAlert: (state, action) => {
      const newAlert = action.payload;
      if (!state.entities[newAlert.alert_id]) {
        alertsAdapter.addOne(state, newAlert);
        incrementCounters(state.alertCounts, newAlert);
        state.lastUpdated = new Date().toISOString();
      }
    },

    // Single alert update — O(1) counter delta
    updateAlert: (state, action) => {
      const incoming = action.payload;
      const alertId = incoming.alert_id || incoming.id;
      const existing = state.entities[alertId];

      if (existing) {
        // Decrement old counters
        decrementCounters(state.alertCounts, existing);
        // Merge with suppression field preservation
        const merged = mergeSuppression(existing, incoming);
        alertsAdapter.upsertOne(state, { ...merged, alert_id: alertId });
        // Increment new counters
        incrementCounters(state.alertCounts, state.entities[alertId]);
        state.lastUpdated = new Date().toISOString();
      }
    },

    // Single alert removal — O(1) counter update
    removeAlert: (state, action) => {
      const alertId = action.payload;
      const existing = state.entities[alertId];
      if (existing) {
        decrementCounters(state.alertCounts, existing);
        alertsAdapter.removeOne(state, alertId);
        state.lastUpdated = new Date().toISOString();
      }
    },

    // Batch update — full recalculation at end
    batchUpdateAlerts: (state, action) => {
      const updates = action.payload;

      updates.forEach((update) => {
        const { type, data } = update;

        switch (type) {
          case 'alert_created':
            if (!state.entities[data.alert_id]) {
              alertsAdapter.addOne(state, data);
            }
            break;

          case 'alert_updated': {
            // Upsert — handles both updates to existing alerts AND alerts
            // we don't have yet (e.g., created during a brief WS disconnect)
            const existing = state.entities[data.alert_id];
            alertsAdapter.upsertOne(state, existing ? mergeSuppression(existing, data) : data);
            break;
          }

          case 'alert_resolved': {
            const existing = state.entities[data.alert_id];
            if (existing) {
              alertsAdapter.upsertOne(state, { ...existing, ...data, status: 'resolved' });
            }
            break;
          }
        }
      });

      // Full recalculation for batch — simpler and correct
      const allAlerts = alertsAdapter.getSelectors().selectAll(state);
      state.alertCounts = calculateAlertCounts(allAlerts);
      state.lastUpdated = new Date().toISOString();
    },

    // Suppression profile management
    setSuppressionProfiles: (state, action) => {
      state.suppressionProfiles = action.payload;
    },

    setActiveProfile: (state, action) => {
      state.activeProfile = action.payload;
    },

    updateSuppressionProfile: (state, action) => {
      const updatedProfile = action.payload;
      const index = state.suppressionProfiles.findIndex(
        (profile) => profile.id === updatedProfile.id
      );

      if (index !== -1) {
        state.suppressionProfiles[index] = updatedProfile;
        if (state.activeProfile && state.activeProfile.id === updatedProfile.id) {
          state.activeProfile = updatedProfile;
        }
      }
    },

    // View settings management
    setSuppressionFilter: (state, action) => {
      state.viewSettings.suppressionFilter = action.payload;
    },

    setViewMode: (state, action) => {
      state.viewSettings.viewMode = action.payload;
    },

    updateViewSettings: (state, action) => {
      state.viewSettings = { ...state.viewSettings, ...action.payload };
    },

    // Connection and loading states
    setLoading: (state, action) => {
      state.loading = action.payload;
    },

    setError: (state, action) => {
      state.error = action.payload;
    },

    setConnectionStatus: (state, action) => {
      state.connectionStatus = action.payload;
    },

    // Pending updates for batching
    addPendingUpdate: (state, action) => {
      state.pendingUpdates.push(action.payload);
    },

    clearPendingUpdates: (state) => {
      state.pendingUpdates = [];
    },

    // Force count recalculation (safety net)
    recalculateCounts: (state) => {
      const allAlerts = alertsAdapter.getSelectors().selectAll(state);
      state.alertCounts = calculateAlertCounts(allAlerts);
    },

    // Alert claiming — O(1) lookup
    alertClaimed: (state, action) => {
      const { alert_id, claimed_by, claimed_by_name, claimed_at } = action.payload;
      const existing = state.entities[alert_id];
      if (existing) {
        alertsAdapter.updateOne(state, {
          id: alert_id,
          changes: { claimed_by, claimed_by_name, claimed_at },
        });
        state.lastUpdated = new Date().toISOString();
      }
    },

    alertClaimReleased: (state, action) => {
      const { alert_id } = action.payload;
      const existing = state.entities[alert_id];
      if (existing) {
        alertsAdapter.updateOne(state, {
          id: alert_id,
          changes: { claimed_by: null, claimed_by_name: null, claimed_at: null },
        });
        state.lastUpdated = new Date().toISOString();
      }
    },

    // Reset state
    resetAlertState: () => {
      return { ...initialState };
    },

    setLastUpdated: (state, action) => {
      state.lastUpdated = action.payload;
    },

    // WebSocket message handler — O(1) lookups
    handleWebSocketMessage: (state, action) => {
      const { type, data } = action.payload;

      switch (type) {
        case 'alert_created':
          if (!state.entities[data.alert_id]) {
            alertsAdapter.addOne(state, data);
            incrementCounters(state.alertCounts, data);
            state.lastUpdated = new Date().toISOString();
          }
          break;

        case 'alert_updated': {
          const existing = state.entities[data.alert_id];
          if (existing) {
            decrementCounters(state.alertCounts, existing);
            alertsAdapter.upsertOne(state, mergeSuppression(existing, data));
            incrementCounters(state.alertCounts, state.entities[data.alert_id]);
            state.lastUpdated = new Date().toISOString();
          }
          break;
        }

        case 'alert_resolved': {
          const existing = state.entities[data.alert_id];
          if (existing) {
            decrementCounters(state.alertCounts, existing);
            alertsAdapter.upsertOne(state, { ...existing, ...data, status: 'resolved' });
            incrementCounters(state.alertCounts, state.entities[data.alert_id]);
            state.lastUpdated = new Date().toISOString();
          }
          break;
        }

        case 'connection_status':
          state.connectionStatus = data.status || 'connected';
          break;

        case 'alert_claimed': {
          const existing = state.entities[data.alert_id];
          if (existing) {
            alertsAdapter.updateOne(state, {
              id: data.alert_id,
              changes: {
                claimed_by: data.claimed_by,
                claimed_by_name: data.claimed_by_name,
                claimed_at: data.claimed_at,
              },
            });
            state.lastUpdated = new Date().toISOString();
          }
          break;
        }

        case 'alert_claim_released': {
          const existing = state.entities[data.alert_id];
          if (existing) {
            alertsAdapter.updateOne(state, {
              id: data.alert_id,
              changes: { claimed_by: null, claimed_by_name: null, claimed_at: null },
            });
            state.lastUpdated = new Date().toISOString();
          }
          break;
        }

        default:
          console.warn('Unknown WebSocket message type:', type);
          break;
      }
    },
  },
});

export const {
  setAlerts,
  mergeAlerts,
  addAlert,
  updateAlert,
  removeAlert,
  batchUpdateAlerts,
  setSuppressionProfiles,
  setActiveProfile,
  updateSuppressionProfile,
  setSuppressionFilter,
  setViewMode,
  updateViewSettings,
  setLastUpdated,
  handleWebSocketMessage,
  setLoading,
  setError,
  setConnectionStatus,
  addPendingUpdate,
  clearPendingUpdates,
  recalculateCounts,
  resetAlertState,
  alertClaimed,
  alertClaimReleased,
} = alertSlice.actions;

// --- Entity adapter selectors (normalized state) ---
const adapterSelectors = alertsAdapter.getSelectors((state) => state?.alerts || initialState);

// Base selectors — these read directly from state, no computation
export const selectAlerts = adapterSelectors.selectAll;
export const selectAllAlerts = adapterSelectors.selectAll; // Alias
export const selectAlertById = adapterSelectors.selectById;
export const selectAlertIds = adapterSelectors.selectIds;
export const selectAlertEntities = adapterSelectors.selectEntities;
export const selectAlertCounts = (state) => state?.alerts?.alertCounts || initialCounts;
export const selectSuppressionProfiles = (state) => state?.alerts?.suppressionProfiles || [];
export const selectActiveProfile = (state) => state?.alerts?.activeProfile || null;
export const selectViewSettings = (state) =>
  state?.alerts?.viewSettings || initialState.viewSettings;
export const selectSuppressionFilter = (state) =>
  state?.alerts?.viewSettings?.suppressionFilter || 'all';
export const selectViewMode = (state) => state?.alerts?.viewSettings?.viewMode || 'realtime';
export const selectLoading = (state) => state?.alerts?.loading || false;
export const selectError = (state) => state?.alerts?.error || null;
export const selectConnectionStatus = (state) => state?.alerts?.connectionStatus || 'disconnected';
export const selectLastUpdated = (state) => state?.alerts?.lastUpdated || null;
export const selectPendingUpdates = (state) => state?.alerts?.pendingUpdates || [];

// --- Memoized computed selectors (only recalculate when inputs change) ---

export const selectFilteredAlerts = createSelector(
  [selectAlerts, selectViewSettings],
  (alerts, viewSettings) => {
    const { suppressionFilter, viewMode } = viewSettings;

    // Dedicated view mode always shows suppressed only
    if (viewMode === 'dedicated') {
      return alerts.filter((alert) => alert.is_suppressed);
    }

    switch (suppressionFilter) {
      case 'normal':
        return alerts.filter((alert) => !alert.is_suppressed);
      case 'suppressed':
        return alerts.filter((alert) => alert.is_suppressed);
      case 'all':
      default:
        return alerts;
    }
  }
);

export const selectCategorizedAlerts = createSelector([selectFilteredAlerts], (alerts) => ({
  new: alerts
    .filter((alert) => alert.status === 'new')
    .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp)),
  investigating: alerts
    .filter((alert) => alert.status === 'investigating')
    .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp)),
  resolved: alerts
    .filter((alert) => alert.status === 'resolved')
    .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp)),
  suppressed: alerts
    .filter((alert) => alert.is_suppressed)
    .sort(
      (a, b) =>
        new Date(a.suppressed_at || a.timestamp) - new Date(b.suppressed_at || b.timestamp)
    ),
}));

export const selectAlertsByPriority = createSelector([selectFilteredAlerts], (alerts) => ({
  Critical: alerts.filter((alert) => alert.priority === 'Critical'),
  Normal: alerts.filter((alert) => alert.priority === 'Normal'),
  Low: alerts.filter((alert) => alert.priority === 'Low'),
}));

// Lane counts selector — used by AlertsManagement hero section and CompactLaneFilter
export const selectLaneCounts = createSelector([selectAlerts], (alerts) => {
  const suppressedAlerts = alerts.filter((alert) => alert.is_suppressed);
  const suppressedGroups = new Set(
    suppressedAlerts.map((alert) => `${alert.alert_type}_${alert.device_id || alert.device?.id}`)
  ).size;

  return {
    priority1: alerts.filter(
      (alert) => alert.status === 'new' && alert.priority === 'Critical' && !alert.is_suppressed
    ).length,
    priority2: alerts.filter(
      (alert) =>
        alert.status === 'new' &&
        (alert.priority === 'High' || alert.priority === 'Normal') &&
        !alert.is_suppressed
    ).length,
    priority3: alerts.filter(
      (alert) =>
        alert.status === 'new' &&
        (alert.priority === 'Medium' || alert.priority === 'Low') &&
        !alert.is_suppressed
    ).length,
    investigating: alerts.filter(
      (alert) => alert.status === 'investigating' && !alert.is_suppressed
    ).length,
    resolved: alerts.filter((alert) => alert.status === 'resolved' && !alert.is_suppressed).length,
    suppressed: suppressedGroups,
    suppressedRaw: suppressedAlerts.length,
  };
});

export const selectTotalActiveAlerts = createSelector([selectAlerts], (alerts) =>
  alerts.filter((alert) => alert.status !== 'resolved').length
);

export const selectAlertsByStatus = createSelector([selectAlerts], (alerts) => ({
  new: alerts.filter((alert) => alert.status === 'new'),
  acknowledged: alerts.filter((alert) => alert.status === 'acknowledged'),
  investigating: alerts.filter((alert) => alert.status === 'investigating'),
  resolved: alerts.filter((alert) => alert.status === 'resolved'),
}));

export default alertSlice.reducer;
